import dayjs, { type Dayjs } from 'dayjs';
import type { Reminder } from './types/reminder';

export type ReminderFormValues = {
  message: string;
  timePreset: number | 'custom';
  customTime?: Dayjs;
  softReminderMinutes: number | null;
  selectedStudents: string[];
};

export type ReminderCreatePayload = Omit<Reminder, 'reminderId' | 'updatedAt'>;

export const REMINDER_PRESET_MINUTES = [5, 10, 15, 20, 25, 30] as const;

const PRESET_MATCH_TOLERANCE_MS = 60 * 1000;

export function reminderEditTimeInitialValues(
  reminder: Reminder
): Pick<ReminderFormValues, 'timePreset' | 'customTime'> {
  const deltaMs = reminder.finalReminderTime - reminder.createdAt;
  for (const m of REMINDER_PRESET_MINUTES) {
    const expected = m * 60 * 1000;
    if (Math.abs(deltaMs - expected) <= PRESET_MATCH_TOLERANCE_MS) {
      return { timePreset: m };
    }
  }
  return {
    timePreset: 'custom',
    customTime: dayjs(reminder.finalReminderTime),
  };
}

export function formValuesToReminderPayload(
  values: ReminderFormValues,
  classId: string,
  teacherId: string
): ReminderCreatePayload {
  const nowDate = dayjs();
  const now = nowDate.valueOf();
  const currentDate = nowDate.startOf('day');
  let finalReminderTime: number;
  if (values.timePreset === 'custom' && values.customTime) {
    finalReminderTime = currentDate
      .hour(values.customTime.hour())
      .minute(values.customTime.minute())
      .second(0)
      .millisecond(0)
      .valueOf();
  } else {
    const mins = typeof values.timePreset === 'number' ? values.timePreset : 5;
    finalReminderTime = nowDate.add(mins, 'minute').second(0).millisecond(0).valueOf();
  }
  const softReminderTime =
    values.softReminderMinutes != null
      ? now + values.softReminderMinutes * 60 * 1000
      : null;
  if (softReminderTime != null && softReminderTime >= finalReminderTime) {
    throw new Error('Soft reminder must be before final reminder');
  }
  const selectedStudents = values.selectedStudents ?? [];
  const targetAllStudents = selectedStudents.length === 0;
  return {
    classId,
    teacherId,
    message: values.message.trim(),
    reminderAt: dayjs(finalReminderTime).toDate().toISOString(),
    finalReminderTime,
    softReminderTime,
    selectedStudents: targetAllStudents ? [] : selectedStudents,
    targetAllStudents,
    status: 'active',
    createdAt: now,
  };
}
